import React, { useState, useEffect, useCallback } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Box,
} from '@mui/material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';

interface Department {
    id: number;
    name: string;
}

interface DepartmentSelectorProps {
    value: number | null;
    onChange: (departmentId: number | null) => void;
    companyId?: number | null;
    disabled?: boolean;
}

const baseApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const API_URL = baseApiUrl.endsWith('/api') ? baseApiUrl : `${baseApiUrl}/api`;

export const DepartmentSelector: React.FC<DepartmentSelectorProps> = ({
    value,
    onChange,
    companyId,
    disabled = false,
}) => {
    const [departments, setDepartments] = useState<Department[]>([]);
    const [loading, setLoading] = useState(true);

    const getToken = () => localStorage.getItem('token');

    const loadDepartments = useCallback(async () => {
        const token = getToken();
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const params = new URLSearchParams();
            if (companyId) {
                params.append('companyId', companyId.toString());
            }

            const response = await fetch(`${API_URL}/analytics/departments?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept': 'application/json',
                },
            });

            if (response.ok) {
                const data = await response.json();
                setDepartments(data.departments || []);
            }
        } catch (err) {
            console.error('DepartmentSelector: Error loading departments:', err);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        setLoading(true);
        onChange(null);
        loadDepartments();
    }, [loadDepartments]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={20} />
            </Box>
        );
    }

    if (departments.length === 0) {
        return null;
    }

    return (
        <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <AccountTreeIcon fontSize="small" />
                    Abteilung
                </Box>
            </InputLabel>
            <Select
                value={value === null ? '' : value}
                label="Abteilung"
                onChange={(e) => {
                    const newValue = e.target.value as string | number;
                    onChange(newValue === '' ? null : Number(newValue));
                }}
            >
                <MenuItem value="">
                    <em>Alle Abteilungen</em>
                </MenuItem>
                {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                        {dept.name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default DepartmentSelector;
